import { z } from 'zod';
import type { CartAmazonDeal } from '../types/deals';

const cartAmazonDealSchema = z.object({
  state: z.enum(['deal', 'prime', 'savings']),
  badge: z.string().optional(),
  startTime: z.string().optional(),
  endTime: z.string().optional(),
  savingsPercent: z.number().optional(),
  savingBasisLabel: z.string().optional(),
  basisAmount: z.number().optional(),
  price: z.number().optional(),
  ccy: z.string().optional(),
  asOf: z.number(),
});

export function parseCartAmazonDeal(raw: unknown): CartAmazonDeal | null {
  const parsed = cartAmazonDealSchema.safeParse(raw);
  return parsed.success ? (parsed.data as CartAmazonDeal) : null;
}

// A null is kept: the ASIN has no deal now, which must replace an older search deal.
export const getCartDeals = async (
  cartId: string,
): Promise<Record<string, CartAmazonDeal | null>> => {
  const res = await fetch(`/api/deals/cart/${encodeURIComponent(cartId)}`, { cache: 'no-store' });
  if (!res.ok) throw new Error(`Error fetching cart deals: HTTP ${res.status}`);
  const data: unknown = await res.json();
  const deals = (data as { deals?: unknown } | null)?.deals;
  if (!deals || typeof deals !== 'object' || Array.isArray(deals)) return {};
  return Object.keys(deals).reduce<Record<string, CartAmazonDeal | null>>((valid, asin) => {
    const raw = (deals as Record<string, unknown>)[asin];
    const deal = parseCartAmazonDeal(raw);
    if (deal || raw === null) valid[asin.trim().toUpperCase()] = deal;
    return valid;
  }, {});
};
